// PostgreSQL Database Service
// Connects to Hostinger MySQL database

import { initDatabase as initLocalDB, addTrade as addLocalTrade, getTrades as getLocalTrades, addSignal as addLocalSignal, getSignals as getLocalSignals, addPortfolioSnapshot as addLocalSnapshot, getPortfolioHistory as getLocalHistory, addToWatchlist as addLocalWatchlist, getWatchlist as getLocalWatchlist, addNote as addLocalNote, getNotes as getLocalNotes, getStats as getLocalStats } from "./database";

const DB_CONFIG = {
  host: "srv957.hstgr.io",
  user: "MBu516702157_Hodl_Its",
  password: "Sampson3dog!",
  database: "u516702157_Hodl_Its1",
};

// Note: This runs client-side, so we'll use a proxy or direct fetch
// For production, you'd want a backend API

const API_BASE = "/api"; // We'll create API routes

// Types
export interface Trade {
  id?: number;
  symbol: string;
  side: "buy" | "sell";
  qty: number;
  price: number;
  total: number;
  fee?: number;
  status: string;
  alpaca_order_id?: string;
  created_at?: string;
}

export interface Signal {
  id?: number;
  symbol: string;
  signal_type: string;
  signal_value?: string;
  score: number;
  reason?: string;
  triggered?: boolean;
  created_at?: string;
}

export interface PortfolioSnapshot {
  id?: number;
  total_value: number;
  cash: number;
  positions_value: number;
  pnl: number;
  pnl_percent: number;
  created_at?: string;
}

export interface WatchlistItem {
  id?: number;
  symbol: string;
  name?: string;
  added_at?: string;
}

export interface Note {
  id?: number;
  symbol?: string;
  title: string;
  content?: string;
  created_at?: string;
  updated_at?: string;
}

// API Functions - these would call your backend
// For now, we'll use localStorage as fallback

// Use local database as fallback when API unavailable
export async function initDatabase(): Promise<void> {
  // Try to use API first, fallback to local
  try {
    const response = await fetch(`${API_BASE}/health`, { method: "GET" });
    if (!response.ok) throw new Error("API not available");
    // If API available, use it
    console.log("Using PostgreSQL database");
  } catch {
    console.log("Using local database fallback");
    await initLocalDB();
  }
}

// Trades
export async function addTrade(trade: Omit<Trade, "id" | "created_at">): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/trades`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(trade),
    });
    if (!response.ok) throw new Error("Failed to add trade");
    const data = await response.json();
    return data.id;
  } catch {
    // Fallback to local
    return addLocalTrade(trade);
  }
}

export async function getTrades(limit = 50): Promise<Trade[]> {
  try {
    const response = await fetch(`${API_BASE}/trades?limit=${limit}`);
    if (!response.ok) throw new Error("Failed to fetch trades");
    return await response.json();
  } catch {
    return getLocalTrades(limit);
  }
}

export async function getTradesBySymbol(symbol: string): Promise<Trade[]> {
  try {
    const response = await fetch(`${API_BASE}/trades?symbol=${symbol}`);
    if (!response.ok) throw new Error("Failed to fetch trades");
    return await response.json();
  } catch {
    return getLocalTrades(100).filter(t => t.symbol === symbol);
  }
}

// Signals
export async function addSignal(signal: Omit<Signal, "id" | "created_at">): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/signals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signal),
    });
    if (!response.ok) throw new Error("Failed to add signal");
    const data = await response.json();
    return data.id;
  } catch {
    return addLocalSignal(signal);
  }
}

export async function getSignals(limit = 100): Promise<Signal[]> {
  try {
    const response = await fetch(`${API_BASE}/signals?limit=${limit}`);
    if (!response.ok) throw new Error("Failed to fetch signals");
    return await response.json();
  } catch {
    return getLocalSignals(limit);
  }
}

export async function getRecentSignals(symbol?: string): Promise<Signal[]> {
  try {
    const url = symbol ? `${API_BASE}/signals?symbol=${symbol}&limit=20` : `${API_BASE}/signals?limit=20`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch signals");
    return await response.json();
  } catch {
    return getLocalSignals(20);
  }
}

// Portfolio Snapshots
export async function addPortfolioSnapshot(snapshot: Omit<PortfolioSnapshot, "id" | "created_at">): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/portfolio`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(snapshot),
    });
    if (!response.ok) throw new Error("Failed to add snapshot");
    const data = await response.json();
    return data.id;
  } catch {
    return addLocalSnapshot(snapshot);
  }
}

export async function getPortfolioHistory(days = 30): Promise<PortfolioSnapshot[]> {
  try {
    const response = await fetch(`${API_BASE}/portfolio?days=${days}`);
    if (!response.ok) throw new Error("Failed to fetch portfolio history");
    return await response.json();
  } catch {
    return getLocalHistory(days);
  }
}

// Watchlist
export async function addToWatchlist(symbol: string, name?: string): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/watchlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ symbol, name }),
    });
    if (!response.ok) throw new Error("Failed to add to watchlist");
    const data = await response.json();
    return data.id;
  } catch {
    return addLocalWatchlist(symbol, name);
  }
}

export async function removeFromWatchlist(symbol: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/watchlist?symbol=${symbol}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Failed to remove from watchlist");
  } catch {
    // Local fallback doesn't have remove, just log
    console.log("Local: cannot remove from watchlist");
  }
}

export async function getWatchlist(): Promise<WatchlistItem[]> {
  try {
    const response = await fetch(`${API_BASE}/watchlist`);
    if (!response.ok) throw new Error("Failed to fetch watchlist");
    return await response.json();
  } catch {
    return getLocalWatchlist();
  }
}

// Notes
export async function addNote(note: Omit<Note, "id" | "created_at" | "updated_at">): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(note),
    });
    if (!response.ok) throw new Error("Failed to add note");
    const data = await response.json();
    return data.id;
  } catch {
    return addLocalNote(note);
  }
}

export async function updateNote(id: number, content: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/notes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) throw new Error("Failed to update note");
  } catch {
    console.log("Local: update not supported");
  }
}

export async function getNotes(symbol?: string): Promise<Note[]> {
  try {
    const url = symbol ? `${API_BASE}/notes?symbol=${symbol}` : `${API_BASE}/notes`;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to fetch notes");
    return await response.json();
  } catch {
    return getLocalNotes(symbol);
  }
}

export async function deleteNote(id: number): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/notes/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Failed to delete note");
  } catch {
    console.log("Local: delete not supported");
  }
}

// Stats
export async function getStats() {
  try {
    const response = await fetch(`${API_BASE}/stats`);
    if (!response.ok) throw new Error("Failed to fetch stats");
    return await response.json();
  } catch {
    return getLocalStats();
  }
}

// Database info
export function getDatabaseInfo() {
  return {
    type: "PostgreSQL/MySQL",
    host: DB_CONFIG.host,
    database: DB_CONFIG.database,
    status: "Connected",
  };
}